// 261005 E3-P3b — MỌI quyết định hiển thị của thẻ khối chặng ③ Bản địa hoá (hàm thuần, có jest). KHÔNG import antd/react.
// Khối ③ "Sẵn sàng Áp ngay": BE xem thử ĐỒNG BỘ $0 (`previewLocalizeOps`) khi AI trả lời ⇒ thẻ hiện sẵn diff + ảnh hưởng; áp = ghi tên + ĐỔI TÊN TRONG KỊCH BẢN ĐÃ VIẾT + đẩy lại Chapters.
// Tái dùng `previewSections` của sửa tay (E3-P3a) để hai nơi nói CÙNG một câu về ảnh hưởng.
import type { AssistantBlock, LocalizeBlockDiff } from '../../../../types/adaptationAssistant';
import type { LocalizePreviewResponse } from '../../../../types/adaptation';
import { PreviewSection, previewSections, skipLine } from '../localizeEditView';
import { friendlyJobNote } from '../jobErrorText';
import { redactMoney } from '../moneyRedact';

const str = (v: unknown): string => String(v ?? '').trim();
const UNDO_EPS_MS = 1;

export type LocalizePhase = 'proposed' | 'ready' | 'stale' | 'failed' | 'applied' | 'restored' | 'dismissed' | 'running';

export interface LocalizeRejectedLine { label: string; reason: string }

export interface LocalizeCardView {
  phase: LocalizePhase;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  headline: string;
  detail: string;
  /** Danh sách lệnh (nhãn VN, theo thứ tự). */
  opLabels: string[];
  /** "Lệnh không áp được": normaliser + xem thử. */
  rejected: LocalizeRejectedLine[];
  /** Một dòng tóm tắt ảnh hưởng ("Thay 12 chỗ trong tập 1, 2 · 2 chương cập nhật · 1 chương giữ nguyên"); rỗng khi chưa có xem thử. */
  summary: string;
  /** Mục gập "Ảnh hưởng chi tiết" (luôn có khi có xem thử): danh sách tập bị đổi, chương giữ nguyên / cập nhật, cảnh báo, lỗi chặn. */
  sections: PreviewSection[];
  /** READY: lỗi chặn mới ⇒ nút Áp tắt kèm lý do. */
  applyBlockedReason: string | null;
  /** Nút Áp bọc Popconfirm (nhắc lại số) — false ⇒ áp ngay. */
  needsConfirm: boolean;
  confirmTitle: string;
  /** Mô tả trong Popconfirm "Áp". */
  confirmDescription: string;
  /** READY có lệnh đổi tên dòng khoá vì lệch giới ⇒ câu cảnh báo vàng "giữ khoá" (thẻ + Popconfirm); null khi không có lệnh đó. */
  keepLockNote: string | null;
  /** APPLIED: lỗi đẩy lại Chapters (cảnh báo vàng) — phần đã ghi KHÔNG bị hoàn tác. */
  handoffWarning: string | null;
  canApply: boolean;
  canPreview: boolean;
  previewLabel: string;
  canUndo: boolean;
  undoReason: string | null;
  undoHint: string;
  canReapply: boolean;
  canDismiss: boolean;
}

export const UNDO_CHANGED_TEXT_LOCALIZE = 'Đã có thay đổi mới hơn sau khi áp — khôi phục qua mục Phiên bản';
export const STALE_TEXT_LOCALIZE = 'Tên / từ vựng đã đổi sau khi xem thử (áp một đề xuất khác, sửa tay, chạy lại…) — xem thử lại để thấy tác động mới';
/** 261023 E3-P2 — op duy nhất đổi tên dòng KHOÁ (chỉ trợ lý, BE kiểm lại điều kiện). Khoá được GIỮ nguyên sau khi áp. */
export const GENDER_RENAME_OP = 'renameLockedForGenderMismatch';
export const KEEP_LOCK_NOTE_LOCALIZE = 'Tên này đang khoá tay. Áp sẽ thay tên và giữ khoá.';
export const CONFIRM_DESCRIPTION_LOCALIZE = 'Ghi tên mới; đổi tên trong kịch bản đã viết và đẩy lại Chapters. Bản cũ còn ở mục Phiên bản.';
/** FE không có `candidate` — nhận ra lệnh qua `command.ops` (cùng danh sách BE lưu vào `candidate.ops`). */
export const hasGenderRenameOp = (block: Pick<AssistantBlock, 'command'>): boolean => (block.command?.ops ?? []).some((o) => str(o?.op) === GENDER_RENAME_OP);

export const UNDO_HINT_LOCALIZE = 'Khôi phục tên về bản trước khi áp: kịch bản đã viết ĐỔI TÊN NGƯỢC và đẩy lại Chapters (chương có Film Plan / đã sửa tay giữ nguyên).';

/** `diff` của khối ③ (thiếu `applied` ⇒ null). */
export const localizeDiffOf = (block: Pick<AssistantBlock, 'diff'>): LocalizeBlockDiff | null => {
  const d = block.diff as unknown as Partial<LocalizeBlockDiff> | null;
  if (!d || typeof d !== 'object' || !Array.isArray(d.applied)) return null;
  return {
    kind: 'localize', applied: d.applied, rejected: Array.isArray(d.rejected) ? d.rejected : [], notes: Array.isArray(d.notes) ? d.notes : [],
    aliasUnmapped: Array.isArray(d.aliasUnmapped) ? d.aliasUnmapped : [], renamePairs: Array.isArray(d.renamePairs) ? d.renamePairs : [],
    bareUnchanged: Array.isArray(d.bareUnchanged) ? d.bareUnchanged : [],
    violations: { errors: d.violations?.errors ?? [], warnings: d.violations?.warnings ?? [] },
    entities: Array.isArray(d.entities) ? d.entities : [],
    glossary: d.glossary ?? { created: [], updated: [], deleted: 0 },
    impact: d.impact ?? { episodes: [], totalReplaced: 0, byPair: [] },
    touchedEpisodes: Array.isArray(d.touchedEpisodes) ? d.touchedEpisodes : [],
    chapters: d.chapters ?? { linkedEpisodes: [], willUpdate: [], skipped: [] },
    world: d.world ?? null,
    info: d.info ?? { unwrittenEpisodes: 0, seasonReaderWillBeStale: false, assistantBlocksWillBeStale: false },
    canApply: d.canApply !== false, blockReason: typeof d.blockReason === 'string' ? d.blockReason : null,
    ...(Array.isArray(d.rejectedOps) ? { rejectedOps: d.rejectedOps } : {}),
  };
};

/** Nhãn lệnh: `command.labels` (BE); thiếu ⇒ tên op. */
export const opLabelsOf = (block: Pick<AssistantBlock, 'command'>): string[] => {
  const ops = block.command?.ops ?? [];
  const labels = block.command?.labels ?? [];
  return ops.map((o, i) => str(labels[i]) || str(o.op));
};

export const localizeRejectedLinesOf = (block: Pick<AssistantBlock, 'command' | 'diff'>): LocalizeRejectedLine[] => [
  ...(block.command?.rejectedOps ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
  ...(localizeDiffOf(block)?.rejected ?? []).map((r) => ({ label: str(r.op), reason: str(r.reason) })),
];

/** Các số của xem thử: chỗ thay, tập bị thay, chương cập nhật / giữ nguyên. */
export const localizeImpactCounts = (d: Pick<LocalizeBlockDiff, 'impact' | 'touchedEpisodes' | 'chapters'>): { replaced: number; episodes: number[]; willUpdate: number; skipped: number } => ({
  replaced: d.impact.totalReplaced,
  episodes: Array.from(new Set<number>([...d.impact.episodes.map((e) => e.episode), ...d.touchedEpisodes.map((t) => t.episode)])).sort((a, b) => a - b),
  willUpdate: d.chapters.willUpdate.length,
  skipped: d.chapters.skipped.length,
});

/** Khối đụng kịch bản đã viết hoặc có chương cập nhật / bị bỏ qua ⇒ Áp cần xác nhận (khớp mức cảnh báo của `LocalizeEditModal`). Không đụng ⇒ áp ngay. */
export const needsConfirm = (d: Pick<LocalizeBlockDiff, 'impact' | 'touchedEpisodes' | 'chapters'> | null): boolean => {
  if (!d) return false;
  const c = localizeImpactCounts(d);
  return c.episodes.length > 0 || c.replaced > 0 || c.willUpdate > 0 || c.skipped > 0;
};

export const confirmTitleOf = (d: Pick<LocalizeBlockDiff, 'impact' | 'touchedEpisodes' | 'chapters'>): string => {
  const c = localizeImpactCounts(d);
  const parts = [`Thay ${c.replaced} chỗ trong ${c.episodes.length} tập đã viết`, `cập nhật ${c.willUpdate} chương`, `giữ nguyên ${c.skipped} chương`];
  return `${parts.join(', ')}. Áp?`;
};

/** "Thay 12 chỗ trong tập 1, 2 · 2 chương cập nhật · 1 chương giữ nguyên (…)". Rỗng khi không đụng gì. */
export const localizeSummaryOf = (d: LocalizeBlockDiff): string => {
  const c = localizeImpactCounts(d);
  const out: string[] = [];
  if (c.episodes.length) out.push(`Thay ${c.replaced} chỗ trong tập ${c.episodes.join(', ')}`);
  if (c.willUpdate) out.push(`${c.willUpdate} chương sẽ cập nhật`);
  if (c.skipped) out.push(`${c.skipped} chương giữ nguyên (${d.chapters.skipped.map((x) => skipLine(x).replace(/\.$/, '')).join('; ')})`);
  const bare = d.bareUnchanged ?? [];
  if (bare.length) out.push(`${bare.reduce((n, b) => n + b.count, 0)} chỗ gọi trống họ chưa đổi`);
  return out.join(' · ');
};

export const NO_SCRIPT_IMPACT_TEXT = 'Không đụng kịch bản đã viết hay Chapters.';

/** Mục "Ảnh hưởng chi tiết" LUÔN có khi có xem thử: khối chỉ đổi từ vựng / khoá (không đụng kịch bản, không chương nào) vẫn nói rõ điều đó. */
const sectionsOf = (d: LocalizeBlockDiff): PreviewSection[] => {
  const out = previewSections({ ...d, hasErrors: !d.canApply, baseModifiedAt: '', written: false } as LocalizePreviewResponse);
  const c = localizeImpactCounts(d);
  if (c.episodes.length === 0 && c.replaced === 0 && c.willUpdate === 0 && c.skipped === 0) out.unshift({ key: 'no-script', tone: 'info', title: 'Ảnh hưởng', lines: [NO_SCRIPT_IMPACT_TEXT] });
  return out;
};

const undoState = (block: AssistantBlock, headModifiedAt: string | null | undefined, busy: boolean, busyReason?: string | null): { canUndo: boolean; undoReason: string | null } => {
  if (busy) return { canUndo: false, undoReason: busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.' };
  const applied = Date.parse(str(block.appliedResult?.modifiedAt));
  const head = Date.parse(str(headModifiedAt));
  if (Number.isNaN(head)) return { canUndo: false, undoReason: 'Đang tải bản địa hoá hiện tại…' };
  if (Number.isNaN(applied) || Math.abs(head - applied) > UNDO_EPS_MS) return { canUndo: false, undoReason: UNDO_CHANGED_TEXT_LOCALIZE };
  return { canUndo: true, undoReason: null };
};

/**
 * Thẻ khối ③. `busy` ⇒ mọi nút GHI (Xem thử, Áp, Khôi phục, Áp lại) tắt; "Bỏ" không bị khoá.
 * READY có lỗi chặn mới (`diff.canApply = false`) ⇒ vẫn hiện để người xem vì sao, nút Áp tắt kèm lý do.
 */
export const localizeCardView = (block: AssistantBlock, busy: boolean, headModifiedAt?: string | null, busyReason?: string | null): LocalizeCardView => {
  const diff = localizeDiffOf(block);
  const rationale = str(block.command?.rationale);
  const base = {
    opLabels: opLabelsOf(block), rejected: localizeRejectedLinesOf(block), summary: diff ? localizeSummaryOf(diff) : '', sections: diff ? sectionsOf(diff) : [],
    applyBlockedReason: null as string | null, needsConfirm: needsConfirm(diff), confirmTitle: diff ? confirmTitleOf(diff) : 'Áp đề xuất này?',
    confirmDescription: CONFIRM_DESCRIPTION_LOCALIZE, keepLockNote: null as string | null,
    handoffWarning: null as string | null, canApply: false, canPreview: false, previewLabel: 'Xem thử lại',
    canUndo: false, undoReason: null as string | null, undoHint: UNDO_HINT_LOCALIZE, canReapply: false, canDismiss: false,
    detail: rationale ? `Lý do: ${rationale}` : '',
  };
  switch (block.state) {
    case 'PROPOSED':
      return { ...base, phase: 'proposed', tone: 'neutral', headline: 'Chưa xem thử', canPreview: !busy, previewLabel: 'Xem thử', canDismiss: true };
    case 'PREVIEW_RUNNING':
      return { ...base, phase: 'running', tone: 'neutral', headline: 'Đang xem thử…', canDismiss: true };   // 261015: BE cho DISMISS từ PREVIEW_RUNNING — khối kẹt phải bỏ được
    case 'FAILED':
      return { ...base, phase: 'failed', tone: 'danger', headline: 'Xem thử lỗi', detail: friendlyJobNote(str(block.error)) || base.detail, canPreview: !busy, canDismiss: true };
    case 'STALE':
      return { ...base, phase: 'stale', tone: 'warn', headline: 'Xem thử đã cũ', detail: STALE_TEXT_LOCALIZE, canPreview: !busy, canDismiss: true };
    case 'READY': {
      const blocked = diff && !diff.canApply ? (diff.blockReason || 'Có lỗi chặn — không áp được') : null;
      const has = !!diff && diff.applied.length > 0;
      // E3-P2: lệnh đổi tên dòng khoá ⇒ BẮT BUỘC xác nhận (kể cả khi không đụng kịch bản) và nói rõ "giữ khoá" ở thẻ + Popconfirm. Diff chung chỉ ghi khoá khi boolean đổi nên không dựa vào nó.
      const keepLock = hasGenderRenameOp(block);
      const impactful = needsConfirm(diff);
      return {
        ...base, phase: 'ready',
        ...(keepLock ? {
          needsConfirm: true, keepLockNote: KEEP_LOCK_NOTE_LOCALIZE, confirmDescription: `${KEEP_LOCK_NOTE_LOCALIZE} ${CONFIRM_DESCRIPTION_LOCALIZE}`,
          confirmTitle: diff && impactful ? confirmTitleOf(diff) : 'Áp đề xuất này?',
        } : {}), tone: blocked ? 'danger' : (diff && (diff.chapters.skipped.length > 0 || !!diff.world) ? 'warn' : 'ok'),
        headline: blocked ? 'Có lỗi chặn' : 'Sẵn sàng áp', applyBlockedReason: blocked,
        canApply: !busy && has && !blocked, canDismiss: true,
      };
    }
    case 'APPLIED': {
      const v = block.appliedResult?.versionNo;
      const u = undoState(block, headModifiedAt, busy, busyReason);
      const err = redactMoney(str(block.appliedResult?.handoffError));
      return {
        ...base, phase: 'applied', tone: err ? 'warn' : 'ok', headline: typeof v === 'number' ? `Đã áp (v${v})` : 'Đã áp', detail: 'Bản cũ vẫn còn ở mục Phiên bản.',
        handoffWarning: err ? `Đã áp, nhưng đẩy lại Chapters lỗi: ${err}` : null, ...u,
      };
    }
    case 'RESTORED': {
      const rv = block.appliedResult?.restoreVersionNo;
      return {
        ...base, phase: 'restored', tone: 'neutral', headline: typeof rv === 'number' ? `Đã gỡ khỏi bản địa hoá (v${rv})` : 'Đã gỡ khỏi bản địa hoá',
        detail: '"Áp lại" không tốn AI khi tên / từ vựng chưa khác lúc đề xuất; nếu đã khác thì cần "Xem thử lại".',
        handoffWarning: str(block.appliedResult?.handoffError) ? `Đã khôi phục, nhưng đẩy lại Chapters lỗi: ${redactMoney(str(block.appliedResult?.handoffError))}` : null,
        canPreview: !busy, canReapply: !busy, canDismiss: true,
      };
    }
    default:
      return { ...base, phase: 'dismissed', tone: 'neutral', headline: 'Đã bỏ', detail: '' };
  }
};
